import React from 'react'
import to from 'await-catch'
import { Formik } from 'formik'
import styled from 'styled-components'
import * as yup from 'yup'
import Select from '../../../components/ui/select'
import { InputsContainer } from '../../../components/ui/form'
import Input from '../../../components/ui/input'
import { formatDateInput } from '../../../utils/formatDate'
import { Calendar, ChevronLeft } from '../../../assets/icons'
import { BackPageHeader } from '../../../components/ui/header'
import { getPatientByClinic, getPatientLogin, patientSignUp } from '../../../api'
import { Button } from '../../../components/ui/button'
import useIsMobile from '../../../hooks/useIsMobile'
import { connect } from 'react-redux'
import { login } from '../../../actions'

const Container = styled.div`
  max-width: 740px;
  width: 100%;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`

const FormContent = styled.div`
  width: 100%;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    min-height: 0;
  }
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  margin: 0 0 24px 0;
  border: none;
  background: none;
  color: ${props => props.theme.colors.brand.blueOmint};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`

const SignUpForm = ({
  appointment,
  patient,
  onNext,
  onBack,
  showBack = false,
  formData,
  genderOptions,
  addFlashMessage,
  login
}) => {
  const isMobile = useIsMobile()
  const validations = yup.object().shape({
    name: yup.string().required('Campo requerido'),
    lastName: yup.string().required('Campo requerido'),
    gender: yup.string().required('Campo requerido'),
    birthDate: yup.string().required('Campo requerido')
  })

  const handleFormSubmit = async values => {
    const tipoDocumentoNombre =
      (patient.tipoDocumento && patient.tipoDocumento.name) || patient.tipoDocumento || ''
    const tipoDocumentoId =
      (patient.tipoDocumento && patient.tipoDocumento.id) || patient.tipoDocumentoId || null
    const nroDocumento = patient.nroDocumento || patient.numeroDocumento || ''

    if (!tipoDocumentoNombre || !nroDocumento) {
      addFlashMessage('No se pudieron validar los datos del documento. Volvé a iniciar sesión.')
      return
    }

    const signUpParams = {
      mail: patient.email || patient.mail,
      nombres: values.name,
      apellidos: values.lastName,
      tipoDocumento: tipoDocumentoNombre,
      documento: nroDocumento,
      sexo: values.gender,
      dob: values.birthDate,
      nroAfiliado: patient.credencial || patient.nroAfiliado,
      obraSocial: patient.obraSocial && patient.obraSocial.id,
      plan: patient.plan && patient.plan.id,
      clinica:
        (appointment && appointment.activeClinic && appointment.activeClinic.codigoEmpresa) || -1,
      telefono: patient.phoneNumber || patient.telefono
    }

    const [err] = await to(patientSignUp(signUpParams))

    if (!err) {
      const obraSocialId =
        (patient.obraSocial && patient.obraSocial.id) ||
        (typeof patient.obraSocial === 'number' ? patient.obraSocial : null)
      const planId =
        (patient.plan && patient.plan.id) || (typeof patient.plan === 'number' ? patient.plan : null)

      const patientByClinicParams = {
        clinica:
          (appointment && appointment.activeClinic && appointment.activeClinic.codigoEmpresa) || -1,
        tipoDoc: tipoDocumentoId,
        nroDoc: nroDocumento
      }

      const request = {
        Documento: nroDocumento,
        TipoDocumento: tipoDocumentoId,
        ObraSocial: obraSocialId,
        Plan: planId,
        Email: patient.email || patient.mail,
        NroAfiliado: patient.credencial || patient.nroAfiliado,
        phone: patient.phone || patient.phoneNumber || patient.telefono
      }

      const [patientErr] = await to(getPatientByClinic(patientByClinicParams))
      const [loginErr, { data } = {}] = await to(getPatientLogin(request))

      const patientExists = Array.isArray(data)
        ? data.length > 0
        : Boolean(data && Object.keys(data).length)

      const tipoDocumentoValue = patient.tipoDocumento
      const selectedTipoDocumento =
        tipoDocumentoValue && typeof tipoDocumentoValue === 'object'
          ? tipoDocumentoValue
          : { id: tipoDocumentoId, name: tipoDocumentoNombre }

      const obraSocialValue =
        patient.obraSocial && typeof patient.obraSocial === 'object'
          ? patient.obraSocial
          : { id: obraSocialId, name: patient.coberturaMedica || '' }

      const planValue =
        patient.plan && typeof patient.plan === 'object'
          ? patient.plan
          : { id: planId, name: patient.planLineaCartilla || '' }

      const patientDetails = {
        ...signUpParams,
        ...(!patientErr && !loginErr && data ? data : {}),
        exists: patientExists,
        mail: request.Email,
        nroDocumento,
        tipoDocumento: selectedTipoDocumento,
        obraSocial: obraSocialValue,
        plan: planValue,
        credencial: request.NroAfiliado,
        phone: request.phone
      }

      login(patientDetails)
      onNext(values)
    }
  }

  return (
    <Container>
      {showBack && (
        <BackButton onClick={onBack} type="button">
          <ChevronLeft size={8} /> Volver
        </BackButton>
      )}
      <Formik
        initialValues={formData}
        validationSchema={validations}
        onSubmit={handleFormSubmit}
        validateOnChange
        validateOnBlur
      >
        {({ values, errors, touched, handleSubmit, handleChange, handleBlur, setFieldValue }) => (
          <FormContent>
            <BackPageHeader
              title="Completá tus datos personales"
              showBack={false}
              marginBottom={isMobile ? '0' : '10px'}
            />
            <InputsContainer maxWidth="360px" gap={24} mb={16} heightMobile="100%">
              <Input
                label="Nombre"
                name="name"
                placeholder="Ingresá tu nombre"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && errors.name}
              />
              <Input
                label="Apellido"
                name="lastName"
                placeholder="Ingresá tu apellido"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.lastName && errors.lastName}
              />
              <Select
                label={'Sexo'}
                name="gender"
                value={values.gender || ''}
                onChange={e => setFieldValue('gender', (e.target && e.target.value) || e)}
                options={genderOptions}
                placeholder={'Seleccioná tu sexo'}
                error={touched.gender && errors.gender}
              />
              <Input
                label={'Fecha de nacimiento'}
                name="birthDate"
                placeholder={'DD/MM/AAAA'}
                value={values.birthDate}
                onChange={e => {
                  const formatted = formatDateInput(e.target.value)
                  setFieldValue('birthDate', formatted)
                }}
                onBlur={handleBlur}
                error={touched.birthDate && errors.birthDate}
                startAdornment={<Calendar />}
                inputMode="numeric"
                pattern="\d*"
                maxLength={10}
              />
              <Button
                onClick={handleSubmit}
                type="submit"
                label={'Continuar'}
                size={isMobile ? 'mobile' : 'xlarge'}
              />
            </InputsContainer>
          </FormContent>
        )}
      </Formik>
    </Container>
  )
}

const mapDispatchToProps = {
  login
}

export default connect(null, mapDispatchToProps)(SignUpForm)
